import { TFunction } from 'i18next';
import validator from 'validator';
import { z } from 'zod';

import { requiredString } from '~/utils/validation/string';

const PROTOCOL_REGEX = /^https?:\/\//i;

export const url = ({ t }) => {
  const zodString = requiredString({ t });

  return zodString.superRefine((value, ctx) => {
    if (!PROTOCOL_REGEX.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('errors:url_1'), // URL must start with http or https
      });

      return z.NEVER;
    }

    if (!validator.isURL(value, { require_protocol: true })) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('errors:url_2'), // 'URL must be valid'
      });

      return z.NEVER;
    }

    return z.NEVER;
  });
};

export const basicUrlRegex = new RegExp(
  '^' +
    '(https?:\\/\\/)?' +
    '([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\\.)*' + // subdomains
    '([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9]))' + // domain
    '\\.([a-zA-Z]{2,63})' + // TLD
    '(\\/[^\\s]*)?' +
    '$',
);

type UrlZodValidationParams = {
  isRequired?: boolean;
  t: TFunction;
};

export const getUrlZodValidation = ({ isRequired = true, t }: UrlZodValidationParams) => {
  return z
    .string()
    .max(1500, t('errors:max', { count: 1500 }))
    .refine((val) => (isRequired ? !!val : true), { message: t('inputs:required') })
    .refine(
      (val) => {
        if (!val && !isRequired) return true;

        if (!basicUrlRegex.test(val)) {
          return false;
        }

        try {
          const url = new URL(val.includes('http') ? val : `http://${val}`);
          const parts = url.hostname.split('.');

          // Check length of each subdomain (before domain + TLD)
          const subdomains = parts.slice(0, -2);
          if (subdomains.some((s) => s.length > 63)) return false;

          const domain = parts[parts.length - 2];
          const tld = parts[parts.length - 1];

          return domain.length <= 63 && tld.length <= 63;
        } catch {
          return false;
        }
      },
      { message: t('errors:url_2') },
    );
};
